import { ArrowDown, Check, X, Handshake, ArrowRightLeft, Ban } from "lucide-react";

/**
 * Mai-2026 — Verhandlungs-Timeline für Provisionsvorschläge.
 *
 * Rendert die ``negotiation_history`` als kompakte vertikale Liste:
 *   Vermittler → 300 € · "Käufer aus 80331"
 *   ↓
 *   Händler → Counter 180 € · "Mein Limit"
 *   ↓
 *   Vermittler → Counter 220 €
 *   ↓
 *   Händler → ✅ Accept 220 €
 *
 * Props:
 *   history: Array<{at, actor:"vermittler"|"dealer", action, amount_eur, note}>
 *   compact?: boolean (kleinere Schrift / weniger Padding)
 */
const ACTION_META = {
    propose:  { icon: Handshake,       cls: "bg-amber-100 text-amber-800 border-amber-200",   label: "Vorschlag" },
    counter:  { icon: ArrowRightLeft,  cls: "bg-violet-100 text-violet-800 border-violet-200", label: "Gegenvorschlag" },
    accept:   { icon: Check,           cls: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Angenommen" },
    decline:  { icon: X,               cls: "bg-red-100 text-red-800 border-red-200",         label: "Abgelehnt" },
    cancel:   { icon: Ban,             cls: "bg-gray-100 text-gray-600 border-gray-300",      label: "Zurückgezogen" },
};

const ACTOR_LABEL = {
    vermittler: "Vermittler",
    dealer: "Händler",
};

export default function NegotiationTimeline({ history, compact = false, testId = "negotiation-timeline" }) {
    if (!Array.isArray(history) || history.length === 0) return null;

    return (
        <ol className={`relative pl-1 ${compact ? "text-[10px]" : "text-[11px]"}`} data-testid={testId}>
            {history.map((step, idx) => {
                const meta = ACTION_META[step.action] || ACTION_META.propose;
                const Icon = meta.icon;
                const actor = ACTOR_LABEL[step.actor] || step.actor;
                const at = step.at ? new Date(step.at) : null;
                return (
                    <li key={idx} className={`flex items-start gap-2 ${idx < history.length - 1 ? "pb-1.5" : ""}`} data-testid={`${testId}-step-${idx}`}>
                        <div className={`shrink-0 mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-sm border ${meta.cls}`}>
                            <Icon className="w-3 h-3" />
                        </div>
                        <div className="flex-1 min-w-0 leading-snug">
                            <div className="flex items-baseline gap-1.5 flex-wrap">
                                <span className="font-bold text-gray-900">{actor}</span>
                                <span className={`uppercase tracking-wider font-bold text-[9px] px-1.5 py-0.5 rounded-sm border ${meta.cls}`}>{meta.label}</span>
                                {step.amount_eur != null && (
                                    <span className="font-mono-num font-bold text-gray-900">{Number(step.amount_eur).toLocaleString("de-DE")} €</span>
                                )}
                                {at && (
                                    <span className="text-gray-400 font-mono-num text-[10px]">{at.toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}</span>
                                )}
                            </div>
                            {step.note && <div className="text-gray-600 italic mt-0.5">„{step.note}"</div>}
                        </div>
                        {idx < history.length - 1 && (
                            <ArrowDown className="absolute left-1 w-3 h-3 text-gray-300" style={{ top: `${(idx + 1) * (compact ? 22 : 26) - 4}px` }} />
                        )}
                    </li>
                );
            })}
        </ol>
    );
}
